/**
    * @description      :
    * @author           : verdentum
    * @group            :
    * @created          : 02/08/2021 - 12:50:15
    *
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 02/08/2021
    * - Author          : verdentum
    * - Modification    :
    * - Version         : 1.1.0
    * - Date            : 2026-09
    * - Modification    : Added getOrgAdminNavData() + the role-6 branch at
    *                      the top of getNavData(). An org's own head user
    *                      (role 6) gets a STATIC nav — Programs + User
    *                      Management, mirroring the Client shell's static
    *                      _nav.ts — rather than the dynamic, menu-DB-driven
    *                      nav every other org role uses below. Reasoning:
    *                      a brand-new org has zero rows in the menu-
    *                      assignment tables (neither the legacy org_menu/
    *                      vd_menu_assignment system nor the newer
    *                      vd_menu_set/vd_menu_set_item engine), so an org
    *                      admin logging in for the first time would hit a
    *                      completely empty header/sidebar under the dynamic
    *                      path — this sidesteps that entirely rather than
    *                      requiring every new org to be seeded with menu
    *                      rows before its own admin can do anything.
    *                      This is a single-file change: getNavData() is
    *                      called with zero arguments from user.component.ts
    *                      and already reads USER_DATA from localStorage
    *                      itself, so the role check lives right here rather
    *                      than needing user.component.ts touched at all.
    * - Version         : 1.2.0
    * - Date            : 2026-09
    * - Modification    : Fixed all `url` values in getOrgAdminNavData() from
    *                      absolute ('/user/program/view') to relative
    *                      ('program/view') paths. user.component.ts's
    *                      getChildMenu() — the shared header-click handler,
    *                      not touched here — hardcodes
    *                      `this.router.navigate(['user/' + target])`, where
    *                      `target` is a child item's `url` taken verbatim.
    *                      An absolute url there produces a doubled,
    *                      malformed path ('user//user/program/view') and
    *                      404s. The sidebar itself doesn't go through this
    *                      method (CoreUI's <app-sidebar-nav> renders
    *                      routerLink directly off the same field), which is
    *                      why sidebar clicks worked while header clicks
    *                      didn't — two consumers of the same `url` field
    *                      with different format expectations. Relative,
    *                      unprefixed paths are what the existing dynamic
    *                      menu system's own menu_link values must already
    *                      use (it's the only way getChildMenu's hardcoded
    *                      prefix ever produces a valid route for a regular
    *                      org user), so this now matches that same
    *                      established convention instead of inventing a
    *                      different one.
**/
import { INavData } from '@coreui/angular';

// Static nav for an org's own head user (role 6) — same shape/spirit as
// client/_nav.ts's getClientNavData(), pointed at the REAL existing routes
// under /user/program/* and /user/usermgmt/* (confirmed against
// user-routing.module.ts, not guessed). Deliberately a SUBSET of
// usermgmt's full route list: create/manage/manage-disabled are the
// real top-level entry points wanted here; map, manage-details and
// map-location are left out — map wasn't wanted at all, and the other two
// are detail/drill-in views reached FROM manage, not menu destinations of
// their own — same reasoning client/_nav.ts used for leaving Program's
// sub-detail routes out of its own top-level items.
//
// URL FORMAT: relative, no leading '/user/' — see the v1.2.0 note above.
// getChildMenu() in user.component.ts prepends 'user/' itself.
function getOrgAdminNavData(): { headerItems: any[]; navItems: any[] } {

  const headerItems: any[] = [
    {
      menu_level: 0,
      menu_id: 'orgadmin-programs',
      name: 'Programs',
      url: 'program/view',
      type: 0,
      content: 'Create and manage this organization\'s programs',
      children: [
        {
          menu_level: 1, menu_id: 'orgadmin-programs-view', parent_menu: 'orgadmin-programs',
          name: 'View Programs', url: 'program/view', type: 0,
          content: 'All programs tagged to this organization',
        },
        {
          menu_level: 1, menu_id: 'orgadmin-programs-create', parent_menu: 'orgadmin-programs',
          name: 'Create Program', url: 'program/create', type: 0,
          content: 'Add a new program',
        },
        {
          menu_level: 1, menu_id: 'orgadmin-programs-edit', parent_menu: 'orgadmin-programs',
          name: 'Edit Program', url: 'program/edit', type: 0,
          content: 'Edit an existing program — locked out for any program a Client created directly',
        },
        {
          menu_level: 1, menu_id: 'orgadmin-programs-delete', parent_menu: 'orgadmin-programs',
          name: 'Delete Program', url: 'program/delete', type: 0,
          content: 'Delete a program — locked out for any program a Client created directly',
        },
      ],
    },
    {
      menu_level: 0,
      menu_id: 'orgadmin-usermgmt',
      name: 'User Management',
      url: 'usermgmt/manage',
      type: 0,
      content: 'Add and manage staff under this organization',
      children: [
        {
          menu_level: 1, menu_id: 'orgadmin-usermgmt-create', parent_menu: 'orgadmin-usermgmt',
          name: 'Create User', url: 'usermgmt/create', type: 0,
          content: 'Add a new staff member to this organization',
        },
        {
          menu_level: 1, menu_id: 'orgadmin-usermgmt-manage', parent_menu: 'orgadmin-usermgmt',
          name: 'Manage Users', url: 'usermgmt/manage', type: 0,
          content: 'View and edit existing staff',
        },
        {
          menu_level: 1, menu_id: 'orgadmin-usermgmt-manage-disabled', parent_menu: 'orgadmin-usermgmt',
          name: 'Manage Disabled Users', url: 'usermgmt/manage-disabled', type: 0,
          content: 'Reactivate or review deactivated staff',
        },
      ],
    },
  ];

  // Same convention the dynamic path below uses: navItemsServer only ever
  // starts pre-populated with the FIRST header item (pushed whole, not just
  // its .children — CoreUI's sidebar-nav renders the nested children itself).
  const navItems: any[] = [headerItems[0]];

  return { headerItems, navItems };
}

export function getNavData(): { headerItems: any[]; navItems: any[] } {

  let navItems: INavData[] = [];

  let user:any = localStorage.getItem('USER_DATA');
  let menu = JSON.parse(user);

  // Org admin (role 6) — static nav, see getOrgAdminNavData() above. Every
  // other role falls through to the existing dynamic, menu-DB-driven path
  // unchanged below.
  if (menu && menu.user_role == 6) {
    return getOrgAdminNavData();
  }
  let headerItems = [
    // { menu_level: 0, menu_id: 0, name: 'Post', url: '/user/post',
    //   children: [
    //     { name: 'Global news', url: ' ' },
    //     { name: 'News by region', url: ' ' },
    //     { name: 'Million Acts of Kindness', url: 'http://3.109.148.216/aok/',attributes: { target: '_blank', rel: 'noopener' }},
    //     { name: 'News by SDG', url: ' ' },
    //   ]
    // }
  ],
  navItemsServer = [
    // { name: 'Dashboard', url: '/user/dashboard' }
  ];
  menu = menu?.menu ?  menu?.menu : [];

  // Get all child menus
  let getChildMenu = (parentMenu:any, menu_level:number) => {
    for (const mKey in menu) {
      let menuItem = menu[mKey];
      
      if(menuItem['menu_level'] == (menu_level+1)
      && menuItem['parent_menu'] == parentMenu['menu_id']) {
        let parent = {
          menu_level: menuItem['menu_level'],
          menu_id: menuItem['menu_id'],
          name: (menuItem['display_name'] && menuItem['display_name'].length > 0) ? menuItem['display_name'] : menuItem['menu_name'],
          url: menuItem['menu_link'],
          type: menuItem['link_type'],
          children: [],
          content: menuItem['menu_desc']
        };
        if(menuItem['link_type'] == 1) {
          parent['attributes'] = { target: '_blank', rel: 'noopener' };
        }
        
        let newParent = getChildMenu(parent, parseInt(menuItem['menu_level']));
        if(newParent['children'].length === 0) delete newParent['children'];
        parentMenu['children'].push(newParent);
      }
    }
    
    return parentMenu;
  };
  for (const mKey in menu) {
    let menuItem = menu[mKey];
    if(menuItem['menu_level'] == 0) {
      let parentMenu = {
        menu_level: menuItem['menu_level'],
        menu_id: menuItem['menu_id'],
        name: (menuItem['display_name'] && menuItem['display_name'].length > 0) ? menuItem['display_name'] : menuItem['menu_name'],
        url: menuItem['menu_link'],
        type: menuItem['link_type'],
        children: [],
        content: menuItem['menu_desc']
      };
      if(menuItem['link_type'] == 1) {
        parentMenu['attributes'] = { target: '_blank', rel: 'noopener' };
      }

      let newParentMenu = getChildMenu(parentMenu, parseInt(menuItem['menu_level']));
      if(newParentMenu['children'].length === 0) delete newParentMenu['children'];

      // If first header menu and has children
      if(parseInt(mKey) === 0 && newParentMenu['children'] && newParentMenu['children'].length > 0) {
        // Push children to side nav
        navItemsServer.push(newParentMenu);
      }

      // Push Patent Menus in Header
      headerItems.push(newParentMenu);
    }
  }

  return {
    headerItems: headerItems,
    navItems : navItemsServer
  };
}